// src/constants/orderLabels.ts

import { OrderEventType, OrderStatus } from '@models/order';
import type { ChipProps } from '@mui/material';
import type { UiMeta } from './uiMeta';
import { getUiLabel, getUiMeta, toLabelMap } from './uiMeta';

/* ============================================================================
 *  ProductSaleType user-friendly display labels
 * ========================================================================== */

export {
  PRODUCT_SALE_TYPE_LABELS,
  getProductSaleTypeLabel,
  getProductSaleTypeMeta,
} from '@utils/productLabels';

/* ============================================================================
 *  OrderStatus user-friendly display labels
 * ========================================================================== */

export const ORDER_STATUS_META = {
  [OrderStatus.OK]: {
    label: 'Completed',
    chip: { color: 'success', variant: 'filled' },
    order: 1,
  },
  [OrderStatus.PENDING]: {
    label: 'Pending',
    chip: { color: 'warning', variant: 'outlined' },
    order: 2,
  },
  [OrderStatus.FAILED]: {
    label: 'Failed',
    chip: { color: 'error', variant: 'outlined' },
    order: 3,
  },
  [OrderStatus.FULL_REFUNDED]: {
    label: 'Refunded',
    chip: { color: 'error', variant: 'outlined' },
    order: 4,
  },
  [OrderStatus.PARTIAL_REFUNDED]: {
    label: 'Refunded (Partially)',
    chip: { color: 'warning', variant: 'outlined' },
    order: 5,
  },
} as const satisfies Record<OrderStatus, UiMeta>;

export const ORDER_STATUS_LABELS = toLabelMap(ORDER_STATUS_META);

/**
 * Safely return a display label for OrderStatus.
 */
export const getOrderStatusMeta = (
  status?: OrderStatus | string | null,
) => getUiMeta(ORDER_STATUS_META, status, '');

export const getOrderStatusLabel = (
  status?: OrderStatus | string | null,
): string => getUiLabel(ORDER_STATUS_META, status, '');

/* ============================================================================
 *  OrderStatus MUI Chip color mapping
 *  (Used for tables, detail pages, status badges)
 * ========================================================================== */

export const getOrderStatusChipColor = (
  status: OrderStatus | string,
): ChipProps['color'] => getOrderStatusMeta(status).chip.color;

export const getOrderStatusChipVariant = (
  status: OrderStatus | string,
): ChipProps['variant'] => getOrderStatusMeta(status).chip.variant;

/* ============================================================================
 *  OrderEventType user-friendly display labels
 * ========================================================================== */

export const ORDER_EVENT_TYPE_META = {
  [OrderEventType.PROCESSED]: {
    label: 'Processed',
    description: 'The order was processed successfully.',
    chip: { color: 'success', variant: 'outlined' },
    order: 1,
  },
  [OrderEventType.PARTIAL_REFUND]: {
    label: 'Partial refund',
    description: 'Some items in the order were refunded.',
    chip: { color: 'warning', variant: 'outlined' },
    order: 2,
  },
  [OrderEventType.FULL_REFUND]: {
    label: 'Refunded',
    description: 'The full order was refunded.',
    chip: { color: 'error', variant: 'outlined' },
    order: 3,
  },
  [OrderEventType.USER_MEMO]: {
    label: 'Memo',
    description: 'A memo was added by an administrator on this page.',
    chip: { color: 'info', variant: 'outlined' },
    order: 4,
  },
} as const satisfies Record<OrderEventType, UiMeta>;

export const ORDER_EVENT_TYPE_LABELS = toLabelMap(ORDER_EVENT_TYPE_META);

export const getOrderEventTypeMeta = (
  eventType?: OrderEventType | string | null,
) => getUiMeta(ORDER_EVENT_TYPE_META, eventType, '');

export const getOrderEventTypeLabel = (
  eventType?: OrderEventType | string | null,
): string => getUiLabel(ORDER_EVENT_TYPE_META, eventType, '');
